var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import moment from "moment";
import { useContext } from "react";
import { MyMealsQuantitySelectionMode, } from "../../Account/Subscriptions/subscriptionsUtils/subscriptions-types";
import { getMustReplaceIngredients } from "../../OrderFlow/hooks/RecipeSelection/recipeSelectionUtils";
import { CustomizationContext, } from "../../Shared/components/CustomizeModal/CustomizationContext";
import { buildNutritionRequestBody, getDefaultCustomizeOptions, getRecipeNutrition, processSelectedCustomizeOptions, } from "../../Shared/components/CustomizeModal/CustomizeModal-utils";
import { useLogging } from "../../Shared/hooks/useLogging/useLogging";
import { createKey } from "../../Shared/utils/createKey";
import { doAdHocChanges } from "../utils/MyMeals-utils";
import { useAccountSettingsQuery } from "../../SWR/AccountSettings/useAccountSettingsQuery";
/**
 * Toggles a recipe selection for a given day and program meal, allowing only one recipe to be selected per meal.
 * Clears existing recipe assignments for the specified programMealId before assigning the new one.
 *
 * @param selectedDay - The day currently selected by the user.
 * @param days - The list of all days with meal and recipe data.
 * @param setDays - Function to update the days state.
 * @param updateMealPropertiesPerDay - Function to apply any additional property updates to meals.
 * @param setSelectedDay - Function to update the selected day in the state.
 * @param setDayForContext - Function to set the current day in context.
 * @param setDaysForContext - Function to set the list of all days in context.
 * @param setAssignmentId - Function to set the ID of the new recipe assignment.
 * @returns A function that handles toggling the selected recipe for a given day and meal.
 */
const handleRecipeToggle = (selectedDay, days, setDays, updateMealPropertiesPerDay, setSelectedDay, setDayForContext, setDaysForContext, setAssignmentId) => (recipeId, programMealId, quantity) => {
    const newDays = days.map((day) => {
        // Check if the current day matches the selected day.
        if (day.DateFormatted === selectedDay.DateFormatted) {
            const newDay = Object.assign(Object.assign({}, day), { 
                // Update the meal's quantity for the specified programMealId.
                Meals: day.Meals.map((meal) => {
                    if (meal.ProgramMealId === programMealId) {
                        // Set meal quantity to the provided quantity.
                        meal.Quantity = quantity;
                    }
                    return meal;
                }), Recipes: day.Recipes.map((recipe) => {
                    var _a;
                    // Clear existing recipe assignments for the specified programMealId.
                    if (recipe.ProgramMealId === programMealId) {
                        recipe.Quantity = 0; // Reset quantity to zero.
                        recipe.Assignments = []; // Clear any previous assignments.
                    }
                    // If recipe matches the recipeId and programMealId, proceed with setting it as the selected recipe.
                    if (recipe.RecipeId === recipeId && recipe.ProgramMealId === programMealId) {
                        if (!recipe.Assignments) {
                            recipe.Assignments = []; // Initialize assignments array if it doesn't exist.
                        }
                        // Generate a unique ID for the new recipe assignment.
                        const newAssignmentId = createKey();
                        setAssignmentId && setAssignmentId(newAssignmentId);
                        // Create a new assignment object for the selected recipe.
                        const newAssignment = {
                            AssignmentId: newAssignmentId,
                            Date: moment(day.DateFormatted),
                            IsAutomaticSelection: false,
                            Quantity: quantity,
                            RecipeId: recipe.RecipeId,
                            RecipeName: recipe.RecipeName,
                            RecipeShortName: recipe.RecipeShortName,
                            RecipePrice: recipe.RecipePrice,
                            RecipeAssignmentPrice: (_a = recipe.RecipeAssignmentPrice) !== null && _a !== void 0 ? _a : 0,
                            ProgramMealId: programMealId,
                            SelectedIngredientOptions: [], // Initialize selected ingredient options as empty.
                        };
                        // Check if there are any ingredients that must be replaced.
                        const { ingredientsToReplace } = getMustReplaceIngredients(recipe);
                        if (ingredientsToReplace && ingredientsToReplace.length > 0) {
                            // Get default customization options for ingredients needing replacement.
                            const { options } = getDefaultCustomizeOptions(ingredientsToReplace, recipe, newAssignment);
                            // Submit customization with the selected options.
                            customizationSubmit(selectedDay, setDays, setSelectedDay, setDayForContext, setDaysForContext, updateMealPropertiesPerDay)(recipe.RecipeId, programMealId)(options, undefined, days, newAssignment);
                        }
                        // Add the new assignment to the recipe's assignments.
                        recipe.Assignments.push(newAssignment);
                        recipe.Quantity = quantity; // Set the recipe quantity.
                    }
                    return recipe;
                }) });
            // Update the selected day in both state and context.
            setSelectedDay(newDay);
            setDayForContext(newDay);
            return newDay;
        }
        else {
            return Object.assign({}, day); // Return a copy of the unchanged day.
        }
    });
    // ? Should ad hoc changes be used, if enabled, for toggle/single-select?
    // const allowAdHocChanges = myMealsConfig.MealQuantitySelectionMode === MyMealsQuantitySelectionMode.AdHocChanges;
    // if (allowAdHocChanges) {
    //   doAdHocChanges(newDays, selectedDay, programMealId);
    // }
    // Set the updated days list in state and context.
    setDays(newDays);
    setDaysForContext(newDays);
    updateMealPropertiesPerDay(newDays); // Apply additional updates to meal properties if needed.
};
const incrementRecipeQuantity = (myMealsConfig, selectedDay, days, setDays, updateMealPropertiesPerDay, setSelectedDay, setDayForContext, setDaysForContext, setAssignmentId, assignmentId, dataDogLog) => (recipe, quantity) => (createNewAssignment) => {
    let currentRecipeAssignment = undefined;
    let updatedSelectedDay = undefined;
    const updatedDays = days.map((day) => {
        if (day.DateFormatted !== selectedDay.DateFormatted) {
            return Object.assign({}, day);
        }
        const newMeals = day.Meals.map((meal) => {
            if (meal.ProgramMealId === recipe.ProgramMealId && meal.Quantity <= quantity) {
                return Object.assign(Object.assign({}, meal), { Quantity: quantity });
            }
            return meal;
        });
        const newRecipes = day.Recipes.map((r) => {
            var _a;
            if (r.RecipeId === recipe.RecipeId && r.ProgramMealId === recipe.ProgramMealId) {
                const assignments = r.Assignments ? [...r.Assignments] : [];
                const targetAssignment = assignments === null || assignments === void 0 ? void 0 : assignments.find((a) => !assignmentId || a.AssignmentId === assignmentId);
                let newAssignments;
                if (targetAssignment && !createNewAssignment) {
                    newAssignments = assignments.map((a) => a.AssignmentId === targetAssignment.AssignmentId ? Object.assign(Object.assign({}, a), { Quantity: a.Quantity + 1 }) : a);
                }
                else {
                    const newAssignmentId = createKey();
                    //We keep track of the assignment for customization so we should update the assignmentId when its the first assignment
                    setAssignmentId && setAssignmentId(newAssignmentId);
                    const newAssignment = {
                        AssignmentId: newAssignmentId,
                        Date: moment(day.DateFormatted),
                        IsAutomaticSelection: false,
                        Quantity: 1,
                        RecipeId: r.RecipeId,
                        RecipeName: r.RecipeName,
                        RecipeShortName: r.RecipeShortName,
                        RecipePrice: r.RecipePrice,
                        RecipeAssignmentPrice: (_a = r.RecipeAssignmentPrice) !== null && _a !== void 0 ? _a : 0,
                        ProgramMealId: r.ProgramMealId,
                        SelectedIngredientOptions: [],
                    };
                    currentRecipeAssignment = newAssignment;
                    newAssignments = [...assignments, newAssignment];
                }
                // Calculate the new recipe quantity from its assignments
                const totalAssignmentQuantity = newAssignments.reduce((total, assignment) => total + assignment.Quantity, 0);
                return Object.assign(Object.assign({}, r), { Assignments: newAssignments, 
                    //Recipe quantity will always be the total of all assignments. This is what displays on the ItemCard
                    Quantity: totalAssignmentQuantity });
            }
            return r;
        });
        const newDay = Object.assign(Object.assign({}, day), { Meals: newMeals, Recipes: newRecipes });
        updatedSelectedDay = newDay;
        return newDay;
    });
    // with ad hoc meal selection, the meal quantity is always updated to match the selected recipe quantity
    const allowAdHocChanges = myMealsConfig.MealQuantitySelectionMode === MyMealsQuantitySelectionMode.AdHocChanges;
    if (allowAdHocChanges) {
        doAdHocChanges(updatedDays, selectedDay, recipe.ProgramMealId);
    }
    const { ingredientsToReplace } = getMustReplaceIngredients(recipe);
    // Default customizations need to be applied when the recipe quantity is incremented
    if (ingredientsToReplace && ingredientsToReplace.length > 0) {
        customizationSubmitWithNutrition(recipe, ingredientsToReplace, currentRecipeAssignment, updatedDays, selectedDay, dataDogLog, setDays, setSelectedDay, setDayForContext, setDaysForContext, updateMealPropertiesPerDay);
    }
    else {
        setSelectedDay(updatedSelectedDay);
        setDayForContext(updatedSelectedDay);
        setDays(updatedDays);
        setDaysForContext(updatedDays);
        updateMealPropertiesPerDay(updatedDays);
    }
};
const customizationSubmitWithNutrition = (recipe, ingredientsToReplace, assignment, days, selectedDay, dataDogLog, setDays, setSelectedDay, setDayForContext, setDaysForContext, updateMealPropertiesPerDayCallback) => __awaiter(void 0, void 0, void 0, function* () {
    const { options } = getDefaultCustomizeOptions(ingredientsToReplace, recipe, assignment);
    const nutrition = yield getRecipeNutrition(recipe.RecipeId, buildNutritionRequestBody(options, recipe), dataDogLog);
    customizationSubmit(selectedDay, setDays, setSelectedDay, setDayForContext, setDaysForContext, updateMealPropertiesPerDayCallback)(recipe.RecipeId, recipe.ProgramMealId)(options, undefined, days, assignment, nutrition);
});
const decrementRecipeQuantity = (myMealsConfig, selectedDay, days, setDays, updateMealPropertiesPerDay, assignmentId) => (recipeId, programMealId, quantity) => {
    const newDays = days.map((day) => {
        if (day.DateFormatted === selectedDay.DateFormatted) {
            return Object.assign(Object.assign({}, day), { Meals: day.Meals.map((meal) => {
                    if (meal.ProgramMealId === programMealId && meal.Quantity <= quantity) {
                        meal.Quantity = quantity;
                    }
                    return meal;
                }), Recipes: day.Recipes.map((recipe) => {
                    var _a, _b, _c;
                    if (recipe.RecipeId === recipeId && recipe.ProgramMealId === programMealId) {
                        const targetAssignment = (_a = recipe === null || recipe === void 0 ? void 0 : recipe.Assignments) === null || _a === void 0 ? void 0 : _a.find((a) => a.AssignmentId === assignmentId);
                        if (!!targetAssignment) {
                            targetAssignment.Quantity -= 1;
                        }
                        // if there is only one assignment decrement it
                        else if (((_b = recipe.Assignments) === null || _b === void 0 ? void 0 : _b.length) === 1) {
                            recipe.Assignments[0].Quantity -= 1;
                        }
                        //Remove all assignments without a quantity
                        recipe.Assignments = recipe.Assignments.filter((a) => a.Quantity > 0);
                        //Recipe quantity will always be the total of all assignments. This is what displays on the ItemCard
                        recipe.Quantity = (_c = recipe === null || recipe === void 0 ? void 0 : recipe.Assignments) === null || _c === void 0 ? void 0 : _c.reduce((total, assignment) => total + assignment.Quantity, 0);
                    }
                    return recipe;
                }) });
        }
        else {
            return Object.assign({}, day);
        }
    });
    // with ad hoc meal selection, the meal quantity is always updated to match the selected recipe quantity
    const allowAdHocChanges = myMealsConfig.MealQuantitySelectionMode === MyMealsQuantitySelectionMode.AdHocChanges;
    if (allowAdHocChanges) {
        doAdHocChanges(days, selectedDay, programMealId);
    }
    setDays(newDays);
    updateMealPropertiesPerDay(newDays);
};
const updateRecipeQuantity = (myMealsConfig, selectedDay, days, setDays, updateMealPropertiesPerDay) => (recipeId, programMealId, quantity) => {
    const newDays = days.map((day) => {
        if (day.DateFormatted === selectedDay.DateFormatted) {
            return Object.assign(Object.assign({}, day), { Meals: day.Meals.map((meal) => {
                    if (meal.ProgramMealId === programMealId && meal.Quantity <= quantity) {
                        meal.Quantity = quantity;
                    }
                    return meal;
                }), Recipes: day.Recipes.map((recipe) => {
                    if (recipe.RecipeId === recipeId && recipe.ProgramMealId === programMealId) {
                        recipe.Quantity = quantity;
                    }
                    return recipe;
                }) });
        }
        else {
            return Object.assign({}, day);
        }
    });
    // with ad hoc meal selection, the meal quantity is always updated to match the selected recipe quantity
    const allowAdHocChanges = myMealsConfig.MealQuantitySelectionMode === MyMealsQuantitySelectionMode.AdHocChanges;
    if (allowAdHocChanges) {
        const targetDay = days.find((day) => day.DateFormatted === selectedDay.DateFormatted);
        const targetMeal = targetDay === null || targetDay === void 0 ? void 0 : targetDay.Meals.find((meal) => meal.ProgramMealId === programMealId);
        const newQuantity = targetDay === null || targetDay === void 0 ? void 0 : targetDay.Recipes.filter((recipe) => recipe.ProgramMealId === programMealId).reduce((sum, recipe) => sum + recipe.Quantity, 0);
        if (targetMeal)
            targetMeal.Quantity = newQuantity !== null && newQuantity !== void 0 ? newQuantity : 0;
    }
    setDays(newDays);
    updateMealPropertiesPerDay(newDays);
};
const updateProductQuantity = (selectedDay, days, setDays, amountForOrder, setAmountForOrder, amountsPerDay, setAmountsPerDay, totalQuantitiesPerDay, setTotalQuantitiesPerDay) => (productId, quantity) => {
    let selectedProduct = selectedDay.Products.find((p) => p.ProductId === productId);
    if (selectedProduct) {
        let quantityDifference = quantity - selectedProduct.NewQuantity;
        let priceDifference = quantityDifference * selectedProduct.ProductPrice;
        setAmountForOrder(amountForOrder + priceDifference);
        let newAmountsPerDay = amountsPerDay;
        newAmountsPerDay[selectedDay.DateFormatted] = newAmountsPerDay[selectedDay.DateFormatted] + priceDifference;
        setAmountsPerDay(newAmountsPerDay);
        let newTotalQuantities = totalQuantitiesPerDay;
        let newTotalQuantityForDay = newTotalQuantities[selectedDay.DateFormatted]
            ? newTotalQuantities[selectedDay.DateFormatted]
            : 0;
        newTotalQuantities[selectedDay.DateFormatted] = newTotalQuantityForDay + quantityDifference;
        setTotalQuantitiesPerDay(newTotalQuantities);
    }
    let newDays = days.map((day) => {
        if (day.DateFormatted === selectedDay.DateFormatted) {
            return Object.assign(Object.assign({}, day), { Products: day.Products.map((product) => {
                    if (product.ProductId === productId) {
                        product.NewQuantity = quantity;
                    }
                    return product;
                }) });
        }
        else {
            return Object.assign({}, day);
        }
    });
    setDays(newDays);
};
const updateMealPropertiesPerDay = (setSelectedOrderMealQuantity, setTotalQuantitiesPerDay, setTotalMealQuantityPerDay, setAmountsPerDay, setAmountForOrder) => (newDays) => {
    let mealQuantityPerDay = {};
    let totalMealQuantityForOrder = 0;
    let newAmountsPerDay = {};
    let totalQuantities = {};
    let totalPerOrder = 0;
    for (let i in newDays) {
        let meals = newDays[i].Meals;
        let products = newDays[i].Products;
        let recipes = newDays[i].Recipes;
        let mealAmountPerDay = 0;
        let productPricePerDay = 0;
        let recipeAmountPerDay = 0;
        let totalProductQuantity = 0;
        let totalMealQuantity = 0;
        for (let m in meals) {
            mealAmountPerDay += meals[m].Price * meals[m].Quantity;
            if (meals[m].Quantity > 0) {
                totalMealQuantity += meals[m].Quantity;
            }
        }
        for (let r in recipes) {
            let recipe = recipes[r];
            if (recipes[r].Quantity > 0) {
                let recipePrice = (recipe.RecipeAssignmentPrice - recipe.ProgramMealPrice) * recipe.Quantity;
                recipeAmountPerDay += recipePrice;
            }
        }
        mealQuantityPerDay[newDays[i].DateFormatted] = totalMealQuantity;
        totalMealQuantityForOrder += totalMealQuantity;
        for (let p in products) {
            productPricePerDay += products[p].ProductPrice * products[p].NewQuantity;
            if (products[p].NewQuantity > 0 || products[p].PurchasedQuantity > 0) {
                totalProductQuantity += products[p].NewQuantity;
            }
        }
        if (totalMealQuantity + totalProductQuantity > 0) {
            totalQuantities[newDays[i].DateFormatted] = totalMealQuantity + totalProductQuantity;
        }
        let totalAmountPerDay = mealAmountPerDay + productPricePerDay + recipeAmountPerDay;
        totalPerOrder += totalAmountPerDay;
        newAmountsPerDay[newDays[i].DateFormatted] = totalAmountPerDay;
    }
    setSelectedOrderMealQuantity(totalMealQuantityForOrder);
    setTotalQuantitiesPerDay(totalQuantities);
    setTotalMealQuantityPerDay(mealQuantityPerDay);
    setAmountsPerDay(newAmountsPerDay);
    setAmountForOrder(totalPerOrder);
};
const updateMealQuantity = (selectedDay, days, setDays, updateCallback) => (programMealId, quantity, isSingleSelect) => {
    let resetRecipeSelections = false;
    let shouldEditRecipes = isSingleSelect ? false : true;
    let newDays = days.map((day) => {
        if (day.DateFormatted === selectedDay.DateFormatted) {
            return Object.assign(Object.assign({}, day), { Meals: day.Meals.map((meal) => {
                    if (meal.ProgramMealId === programMealId) {
                        resetRecipeSelections = meal.Quantity > quantity;
                        meal.Quantity = quantity;
                    }
                    return meal;
                }) });
        }
        else {
            return Object.assign({}, day);
        }
    });
    if (shouldEditRecipes) {
        if (resetRecipeSelections) {
            newDays.forEach((day) => {
                if (day.DateFormatted === selectedDay.DateFormatted) {
                    day.Recipes.forEach((recipe) => {
                        if (recipe.ProgramMealId === programMealId) {
                            recipe.Assignments = [];
                            recipe.Quantity = 0;
                        }
                    });
                }
            });
        }
    }
    setDays(newDays);
    updateCallback(newDays);
};
const handleCustomizeToggle = (selectedDay, days, setDaysForContext) => (recipeId, programMealId, quantity) => {
    let newDays = days.map((day) => {
        if (day.DateFormatted === selectedDay.DateFormatted) {
            return Object.assign(Object.assign({}, day), { Recipes: day.Recipes.map((recipe) => {
                    if (recipe.ProgramMealId === programMealId) {
                        recipe.Quantity = 0;
                    }
                    if (recipe.RecipeId === recipeId && recipe.ProgramMealId === programMealId) {
                        recipe.Quantity = quantity;
                    }
                    return recipe;
                }) });
        }
        else {
            return Object.assign({}, day);
        }
    });
    setDaysForContext(newDays);
};
/**
 * Update the recipes in the customize modal
 *
 * @param selectedDay
 * @param days
 * @param setDaysForContext
 * @returns
 */
const updateCustomizationQuantity = (allowAdHocChanges, selectedDay, days, assignmentId, setDaysForContext, setDayForContext, setAssignmentId, setQuantity, setIsAddDisabled) => (recipeId, programMealId, increment) => {
    let newDays = days.map((day) => {
        if (day.DateFormatted === selectedDay.DateFormatted) {
            const targetMeal = day.Meals.find((m) => m.ProgramMealId === programMealId && m.MealDate === selectedDay.DateFormatted);
            const newDay = Object.assign(Object.assign({}, day), { Recipes: day.Recipes.map((recipe) => {
                    var _a, _b, _c, _d, _e;
                    if (recipe.RecipeId === recipeId && recipe.ProgramMealId === programMealId) {
                        const targetAssignment = (_a = recipe === null || recipe === void 0 ? void 0 : recipe.Assignments) === null || _a === void 0 ? void 0 : _a.find((a) => a.AssignmentId === assignmentId);
                        if (!!targetAssignment) {
                            if (increment) {
                                const newQty = (targetAssignment.Quantity += 1);
                                setQuantity(newQty);
                            }
                            else {
                                const currentAssignmentQuantity = targetAssignment.Quantity - 1 > 0 ? (targetAssignment.Quantity -= 1) : 0;
                                targetAssignment.Quantity = currentAssignmentQuantity;
                                setQuantity(currentAssignmentQuantity);
                            }
                        }
                        else if (increment) {
                            const newAssignmentId = createKey();
                            //We keep track of the assignment for customization so we should update the assignmentId when its the first assignment
                            setAssignmentId && setAssignmentId(newAssignmentId);
                            const newAssignment = {
                                AssignmentId: newAssignmentId,
                                Date: moment(day.DateFormatted),
                                IsAutomaticSelection: false,
                                Quantity: 1,
                                RecipeId: recipe.RecipeId,
                                RecipeName: recipe.RecipeName,
                                RecipeShortName: recipe.RecipeShortName,
                                RecipePrice: recipe.RecipePrice,
                                RecipeAssignmentPrice: (_b = recipe.RecipeAssignmentPrice) !== null && _b !== void 0 ? _b : 0,
                                ProgramMealId: programMealId,
                                SelectedIngredientOptions: [],
                            };
                            recipe.Assignments.push(newAssignment);
                            setQuantity(1);
                        }
                        const newRecipeQty = (_c = recipe.Assignments.reduce((total, assignment) => total + assignment.Quantity, 0)) !== null && _c !== void 0 ? _c : 0;
                        recipe.Quantity = newRecipeQty;
                        const assignmentQuantityForMeal = (_d = day.Recipes.filter((r) => r.ProgramMealId === programMealId).reduce((total, recipe) => total + recipe.Quantity, 0)) !== null && _d !== void 0 ? _d : 0;
                        setIsAddDisabled(!((_e = day === null || day === void 0 ? void 0 : day.Config) === null || _e === void 0 ? void 0 : _e.CanChooseMeals) || assignmentQuantityForMeal === (targetMeal === null || targetMeal === void 0 ? void 0 : targetMeal.Quantity));
                    }
                    return recipe;
                }) });
            setDayForContext(newDay);
            return newDay;
        }
        else {
            return Object.assign({}, day);
        }
    });
    // with ad hoc meal selection, the meal quantity is always updated to match the selected recipe quantity
    if (allowAdHocChanges) {
        doAdHocChanges(newDays, selectedDay, programMealId);
    }
    setDaysForContext(newDays);
};
/**
 * Submit form and apply meal customizations
 *
 * @param selectedDay
 * @param setDays
 * @param updateCallback
 * @returns
 */
const customizationSubmit = (selectedDay, setDays, setSelectedDay, setDayForContext, setDaysForContext, updateMealPropertiesPerDay) => (recipeId, programMealId) => (selectedCustomizeOptions, meals, days, assignment, nutrition) => {
    if (!days)
        return;
    const newDays = days.map((day) => {
        if (day.DateFormatted === selectedDay.DateFormatted) {
            const newRecipes = day.Recipes.map((recipe) => {
                if (recipe.RecipeId === recipeId && recipe.ProgramMealId === programMealId) {
                    //Remove all assignments without a quantity
                    recipe.Assignments = recipe.Assignments.filter((a) => a.Quantity > 0);
                    const targetAssignment = recipe.Assignments.find((a) => a.AssignmentId === (assignment === null || assignment === void 0 ? void 0 : assignment.AssignmentId));
                    const { ingredientsToReplace } = getMustReplaceIngredients(recipe);
                    // If the assignment exists use that one. If it doesn't use the new assignment
                    const recipeAssignmentToUse = targetAssignment !== null && targetAssignment !== void 0 ? targetAssignment : assignment;
                    //Clear previous selections
                    if (recipeAssignmentToUse && recipe.Quantity > 0) {
                        recipeAssignmentToUse.SelectedIngredientOptions = [];
                        recipeAssignmentToUse.Nutrition = nutrition;
                    }
                    processSelectedCustomizeOptions(selectedCustomizeOptions, ingredientsToReplace, recipeAssignmentToUse);
                    return recipe;
                }
                return recipe;
            });
            //Current day needs to be updated because that is what renders the current meals/recipes
            setSelectedDay(Object.assign(Object.assign({}, day), { Recipes: newRecipes }));
            setDayForContext(Object.assign(Object.assign({}, day), { Recipes: newRecipes }));
            return Object.assign(Object.assign({}, day), { Recipes: newRecipes });
        }
        else {
            return Object.assign({}, day);
        }
    });
    setDaysForContext(newDays);
    setDays(newDays);
    updateMealPropertiesPerDay(newDays);
};
export const useMyMeals = (myMealsConfig, selectedDay, setSelectedDay, days, setDays, amountForOrder, setAmountForOrder, amountsPerDay, setAmountsPerDay, totalQuantitiesPerDay, setTotalQuantitiesPerDay, setSelectedOrderMealQuantity, setTotalMealQuantityPerDay) => {
    const { accountSettings } = useAccountSettingsQuery();
    const { MyMealsLinkText } = accountSettings !== null && accountSettings !== void 0 ? accountSettings : {};
    const updateCallback = updateMealPropertiesPerDay(setSelectedOrderMealQuantity, setTotalQuantitiesPerDay, setTotalMealQuantityPerDay, setAmountsPerDay, setAmountForOrder);
    const { days: customizedDays, setDays: setDaysForContext, setDay: setDayForContext, setQuantity, setIsAddDisabled, assignmentId, setAssignmentId, } = useContext(CustomizationContext);
    const { datadogLog } = useLogging();
    const allowAdHocChanges = (myMealsConfig === null || myMealsConfig === void 0 ? void 0 : myMealsConfig.MealQuantitySelectionMode) === MyMealsQuantitySelectionMode.AdHocChanges;
    return {
        MyMealsLinkText,
        updateMealPropertiesPerDay: updateCallback,
        updateMealQuantity: updateMealQuantity(selectedDay, days, setDays, updateCallback),
        handleRecipeToggle: handleRecipeToggle(selectedDay, days, setDays, updateMealPropertiesPerDay, setSelectedDay, setDayForContext, setDaysForContext, setAssignmentId),
        handleCustomizeToggle: handleCustomizeToggle(selectedDay, customizedDays, setDaysForContext),
        updateCustomizationQuantity: updateCustomizationQuantity(allowAdHocChanges, selectedDay, customizedDays, assignmentId, setDaysForContext, setDayForContext, setAssignmentId, setQuantity, setIsAddDisabled),
        customizationSubmit: customizationSubmit(selectedDay, setDays, setSelectedDay, setDayForContext, setDaysForContext, updateCallback),
        updateRecipeQuantity: updateRecipeQuantity(myMealsConfig, selectedDay, days, setDays, updateCallback),
        incrementRecipeQuantity: incrementRecipeQuantity(myMealsConfig, selectedDay, days, setDays, updateCallback, setSelectedDay, setDayForContext, setDaysForContext, setAssignmentId, assignmentId, datadogLog),
        decrementRecipeQuantity: decrementRecipeQuantity(myMealsConfig, selectedDay, days, setDays, updateCallback, assignmentId),
        updateProductQuantity: updateProductQuantity(selectedDay, days, setDays, amountForOrder, setAmountForOrder, amountsPerDay, setAmountsPerDay, totalQuantitiesPerDay, setTotalQuantitiesPerDay),
    };
};
